// Форматирането на стойностите в клетките.
//
// Парите минават през общия `fmtEuro` (src/utils/currency.ts) — НЕ се въвежда
// втори валутен формат, иначе тази справка ще показва числата различно от
// съседните. Excel получава ЧИСЛА, не форматирани низове (виж dataTableExport).

import dayjs from "dayjs";

export const EM_DASH = "—";

/** Decimal полетата от BE идват като JSON НИЗ — парсват се явно. */
export const parseDecimal = (
  value: string | number | null | undefined,
): number => {
  if (value === null || value === undefined) return 0;
  const parsed = typeof value === "number" ? value : parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

type MinuteLabels = { hour: string; minute: string };

/** „42 мин" под час, „1 ч 16 мин" над него. */
export const formatMinutes = (
  minutes: number | null | undefined,
  labels: MinuteLabels,
): string => {
  if (minutes === null || minutes === undefined || !Number.isFinite(minutes)) {
    return EM_DASH;
  }
  const total = Math.max(0, Math.round(minutes));
  if (total < 60) return `${total} ${labels.minute}`;
  const hours = Math.floor(total / 60);
  const rest = total % 60;
  return rest
    ? `${hours} ${labels.hour} ${rest} ${labels.minute}`
    : `${hours} ${labels.hour}`;
};

/** Часът на реда; датата излиза на втори ред само когато е поискана. */
export const formatDateTime = (
  value: string | Date | null | undefined,
  withDate: boolean | "auto",
): { time: string; date: string | null } => {
  if (!value) return { time: EM_DASH, date: null };
  const parsed = dayjs(value);
  if (!parsed.isValid()) return { time: EM_DASH, date: null };
  // `auto` — датата излиза само когато стойността НЕ е от днес. Справката
  // филтрира по час на затваряне, тоест и при „днешен" период вътре има
  // сметки, отворени вчера: голо „23:40" не казва кой ден е било.
  const show =
    withDate === "auto" ? !parsed.isSame(dayjs(), "day") : withDate;
  return {
    time: parsed.format("HH:mm"),
    date: show ? parsed.format("DD.MM") : null,
  };
};

// `trim` маха излишните нули след запетаята („0 %", „10 %", но „12.5 %") —
// за проценти, които са въведени от човек (клиентски отстъпки), не сметнати.
export const formatPercent = (
  value: number | null | undefined,
  decimals = 1,
  trim = false,
): string => {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return EM_DASH;
  }
  const fixed = value.toFixed(decimals);
  return `${trim ? String(Number(fixed)) : fixed} %`;
};

/**
 * Число за колона `num`.
 *
 * Без изричен `decimals` форматът е „колкото носи стойността": 3 остава „3",
 * а 0.750 става „0.75". Коравото закръгляне до цяло показваше 0.750 кг като
 * „1" — количеството на екрана не съвпадаше с това в сметката и в Excel.
 */
export const formatNumber = (
  value: number | null | undefined,
  decimals?: number,
): string => {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return EM_DASH;
  }
  if (decimals !== undefined) return value.toFixed(decimals);
  if (Number.isInteger(value)) return String(value);
  return String(Number(value.toFixed(3)));
};
